import React from 'react';
import { useTranslation } from 'react-i18next';

import type { PatientDossierSectionError } from '../../../../../helpers/api/patients/patients';
import {
  getDossierSectionLabel,
  type DossierSectionSummary,
} from '../utils/dossierSectionSummary';

interface PartialErrorsBannerProps {
  errors: PatientDossierSectionError[];
  summary?: DossierSectionSummary | null;
}

export const PartialErrorsBanner: React.FC<PartialErrorsBannerProps> = ({
  errors,
  summary,
}) => {
  const { t } = useTranslation('Patient');
  if (errors.length === 0) return null;

  const savedSections = summary?.saved ?? [];
  const failedSections =
    summary?.failed ??
    errors.map((error) => ({
      ...error,
      label: getDossierSectionLabel(t, error.section),
    }));

  return (
    <div className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
      <div className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-amber-200 text-[11px] font-bold text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
        !
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t('dossier_partial_errors')}</div>
        <p className="mt-1 text-xs">{t('dossier_partial_helper')}</p>

        {savedSections.length > 0 && (
          <div className="mt-2 text-xs">
            <span className="font-semibold">
              {t('dossier_partial_saved_label')}:
            </span>{' '}
            {savedSections.map((section) => section.label).join(', ')}
          </div>
        )}

        <div className="mt-2 text-xs font-semibold">
          {t('dossier_partial_failed_label')}:
        </div>
        <ul className="mt-1 space-y-0.5 text-xs">
          {failedSections.map((section) => (
            <li
              key={`${section.section}-${section.message ?? section.label}`}
              className="flex gap-1"
            >
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[11px] text-amber-900 dark:bg-amber-800/40 dark:text-amber-200">
                {section.label}
              </span>
              <span className="min-w-0 flex-1">{section.message}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
